import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { BsExclamationTriangleFill } from 'react-icons/bs';
import AppContext from 'Utils/AppContext';
import { BRAND, STATUS, INK, FONT_MONO } from 'Utils/styleTokens';
import { getLandscape } from 'AssetManager/manage-api';
import { listDrivers } from 'Drivers/api';
import { engineHasDriver } from 'Utils/driverUtils';
import {
  inferCloud,
  isInternalExposure,
  isLoudExposure,
  rangeProvenanceLabel,
  sortPortExposures,
  portRangeLabel,
} from 'AssetManager/LandscapeUtils';
import {
  resourceExposures,
  connectionForResourcePort,
  ipDnsForResource,
  managerSourceToEditorPrefill,
  encodePrefill,
} from 'SensitiveDataLandscape/UnknownSources/managerSources';
import Breadcrumbs from 'Utils/Breadcrumbs';

const MATCH_LABEL = { exact: 'exact', nearby: 'nearby', non_standard: 'non-standard' };

const HEADER_CELL = { fontSize: 11, fontWeight: 600, letterSpacing: '.08em', color: INK[500] };

/**
 * Per-port view of one surfaced cloud resource. Reached from a pipeline Surfaced card that
 * exposes more than one port: every classified port renders as a row (exact matches first)
 * with its own create-data-source action, since one host can legitimately back several
 * data sources on different ports. Covered ports link to their existing data source; the
 * surfaced card upstream only clears once every port is covered.
 *
 * The resource rides in on location.state; a refresh/deep-link falls back to re-fetching
 * the landscape and finding it by identifier.
 */
function ResourcePortsPage() {
  const { identifier } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { connections } = useContext(AppContext) || {};
  const stateResource = location.state && location.state.resource;
  const [resource, setResource] = useState(stateResource || null);
  const [loading, setLoading] = useState(!stateResource);
  const [drivers, setDrivers] = useState([]);

  const decodedId = decodeURIComponent(identifier || '');

  useEffect(() => {
    let cancelled = false;
    if (!resource) {
      getLandscape()
        .then((res) => {
          if (cancelled) return;
          const sources = Array.isArray(res?.sources) ? res.sources : [];
          setResource(sources.find((s) => String(s.identifier) === decodedId) || null);
          setLoading(false);
        })
        .catch((e) => {
          console.log('getLandscape for resource ports page failed', e);
          if (!cancelled) setLoading(false);
        });
    }
    listDrivers()
      .then((list) => {
        if (!cancelled) setDrivers(Array.isArray(list) ? list : []);
      })
      .catch((e) => console.log('listDrivers for resource ports page failed', e));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedId]);

  const exposures = useMemo(
    () => (resource ? sortPortExposures(resourceExposures(resource)) : []),
    [resource]
  );

  const createForPort = (exp) => {
    const prefill = encodePrefill(
      managerSourceToEditorPrefill(resource, {
        port: exp.port,
        engine: exp.knownDatabase || undefined,
      })
    );
    history.push(`/sources/source-editor?prefill=${encodeURIComponent(prefill)}`);
  };

  if (loading) {
    return (
      <Container fluid className='pt-3'>
        <div className='sg-small' style={{ color: INK[500] }}>Loading resource…</div>
      </Container>
    );
  }

  if (!resource) {
    return (
      <Container fluid className='pt-3'>
        <div className='sg-card' style={{ padding: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Resource not found</div>
          <div className='sg-small' style={{ color: INK[500], marginBottom: 10 }}>
            This resource is no longer in the cloud landscape (it may have been removed or the
            landscape may have been rescanned).
          </div>
          <Breadcrumbs items={[{ label: 'Data Privacy Workflow', to: '/landscape' }, { label: 'Resource not found' }]} />
        </div>
      </Container>
    );
  }

  const name = resource.name || resource.database || resource.identifier || '(unnamed)';
  const host = ipDnsForResource(resource);
  const subtitle = [resource.cloud || inferCloud(resource.source), resource.source, resource.engine]
    .filter(Boolean)
    .join(' · ');

  return (
    <Container fluid className='pt-3'>
      <Breadcrumbs items={[{ label: 'Data Privacy Workflow', to: '/landscape' }, { label: name }]} />
      <div className='sg-card' style={{ padding: '14px 16px' }}>
        <div style={{ fontFamily: FONT_MONO, fontSize: 15, fontWeight: 600, color: 'var(--ink-900)' }}>
          {name}
        </div>
        <div className='sg-small' style={{ color: INK[500], margin: '2px 0 2px' }}>{subtitle}</div>
        {host && (
          <div className='sg-small' style={{ color: INK[500], fontFamily: FONT_MONO }}>{host}</div>
        )}
        <div className='sg-small' style={{ color: INK[500], margin: '10px 0 12px' }}>
          This resource exposes {exposures.length} port{exposures.length === 1 ? '' : 's'}. Create a
          data source for each port that backs a database you want the platform to manage.
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '170px 110px minmax(140px, 1fr) 140px 190px',
            rowGap: 8,
            columnGap: 10,
            alignItems: 'center',
            fontSize: 12.5,
            maxWidth: 860,
          }}
          data-testid='resource-ports-table'
        >
          <div style={HEADER_CELL}>PORT</div>
          <div style={HEADER_CELL}>MATCH</div>
          <div style={HEADER_CELL}>IP RANGE</div>
          <div style={HEADER_CELL}>ENGINE</div>
          <div style={HEADER_CELL} />
          {exposures.map((exp, i) => {
            const covered = connectionForResourcePort(resource, exp, connections);
            const engine = exp.knownDatabase || resource.engine || '';
            const hasDriver = engineHasDriver(engine, drivers);
            const internal = isInternalExposure(exp);
            const loud = isLoudExposure(exp);
            const provenance = rangeProvenanceLabel(exp);
            const muted = internal ? { color: INK[500] } : {};
            return (
              <React.Fragment key={`${exp.port}-${exp.sourceRange}-${i}`}>
                <div style={{ fontFamily: FONT_MONO, color: 'var(--ink-900)', ...muted }}>
                  {loud && (
                    <BsExclamationTriangleFill
                      size={12}
                      color={STATUS.danger.text}
                      style={{ marginRight: 6, verticalAlign: '-1px' }}
                      title={`Port ${exp.port} is reachable from an external source (${exp.sourceRange})`}
                    />
                  )}
                  {portRangeLabel(exp)}
                  {provenance && (
                    <span style={{ marginLeft: 6, fontSize: 11.5, color: INK[500] }}>({provenance})</span>
                  )}
                </div>
                <div style={{ color: INK[500], ...muted }}>
                  {MATCH_LABEL[String(exp.match || '').toLowerCase()] || '—'}
                </div>
                <div
                  style={{ fontFamily: FONT_MONO, color: loud ? STATUS.danger.text : INK[500], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...muted }}
                  title={exp.sourceRange || ''}
                >
                  {exp.sourceRange || '—'}
                </div>
                <div style={{ fontFamily: FONT_MONO, color: INK[500], ...muted }}>{engine || '—'}</div>
                <div style={{ textAlign: 'right' }}>
                  {covered ? (
                    <Link
                      to={`/sources/source-editor/${encodeURIComponent(covered.id)}`}
                      title='Open data source detail'
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 11,
                        fontWeight: 600,
                        color: STATUS.success.text,
                        background: STATUS.success.bg,
                        padding: '5px 12px',
                        borderRadius: 999,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ✓ {covered.name}
                    </Link>
                  ) : !hasDriver ? (
                    <Link
                      to='/drivers'
                      title='No JDBC driver is installed for this engine yet.'
                      style={{
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: STATUS.warning.text,
                        background: STATUS.warning.bg,
                        padding: '6px 14px',
                        borderRadius: 7,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Upload driver
                    </Link>
                  ) : (
                    <button
                      type='button'
                      onClick={() => createForPort(exp)}
                      data-testid={`create-for-port-${exp.port}`}
                      style={{
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: BRAND.blue,
                        background: STATUS.info.bg,
                        border: 'none',
                        padding: '6px 14px',
                        borderRadius: 7,
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                      }}
                    >
                      + Create data source
                    </button>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </Container>
  );
}

export default ResourcePortsPage;
